import { cn } from "@/lib/utils";

// ============================================
// 타입 정의
// ============================================

type ChipSize = "small" | "medium" | "large";

// ============================================
// TextChip 컴포넌트
// ============================================

interface TextChipProps {
  size?: ChipSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** 색상은 className으로 지정 (예: "bg-slate-200 text-slate-600") */
  className?: string;
  children: React.ReactNode;
}

const textChipSizeStyles: Record<ChipSize, string> = {
  small: "h-[18px] px-1 py-0.5 gap-0.5 caption-2-medium",
  medium: "h-5 px-1 py-0.5 gap-0.5 caption-1-medium",
  large: "h-[22px] px-1 py-0.5 gap-0.5 label-2-medium",
};

const textChipIconSizes: Record<ChipSize, string> = {
  small: "size-[10px]", // 6px
  medium: "size-[12px]", // 8px
  large: "size-[16px]", // 10px
};

export const TextChip = ({
  size = "small",
  leftIcon,
  rightIcon,
  className,
  children,
}: TextChipProps) => {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-[4px] font-medium",
        textChipSizeStyles[size],
        className,
      )}
    >
      {leftIcon && (
        <span
          className={cn(
            "flex items-center justify-center shrink-0",
            textChipIconSizes[size],
          )}
        >
          {leftIcon}
        </span>
      )}
      <span className="whitespace-nowrap">{children}</span>
      {rightIcon && (
        <span
          className={cn(
            "flex items-center justify-center shrink-0",
            textChipIconSizes[size],
          )}
        >
          {rightIcon}
        </span>
      )}
    </span>
  );
};

// ============================================
// IconChip 컴포넌트
// ============================================

interface IconChipProps {
  size?: ChipSize;
  /** 색상은 className으로 지정 (예: "bg-orange-500 text-white") */
  className?: string;
  children: React.ReactNode;
}

const iconChipSizeStyles: Record<ChipSize, string> = {
  small: "size-3", // 12px
  medium: "size-4", // 16px
  large: "size-5", // 20px
};

const iconChipIconSizes: Record<ChipSize, string> = {
  small: "size-2", // 8px
  medium: "size-3", // 12px
  large: "size-4", // 16px
};

export const IconChip = ({
  size = "small",
  className,
  children,
}: IconChipProps) => {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-[2px]",
        iconChipSizeStyles[size],
        className,
      )}
    >
      <span
        className={cn(
          "flex items-center justify-center",
          iconChipIconSizes[size],
        )}
      >
        {children}
      </span>
    </span>
  );
};
